import { useState } from "react";
import { SITE } from "@/config/site";
import { StarOrnament } from "@/components/Ornament";
import { BookButton } from "@/components/GoldButton";

const INPUT =
  "min-h-[52px] w-full rounded-xl border border-[#D4AF37]/30 bg-[#0E0C0A] px-4 text-base text-[#F7F4EF] placeholder:text-[#918A7E] focus:border-[#D4AF37] focus:outline-none";
const LABEL = "mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]";

function buildMailto(f) {
  const subject = `Sponsorship Enquiry — ${f.package} — ${f.company || f.name}`;
  const body = [
    `Name: ${f.name}`,
    `Company: ${f.company}`,
    `Phone: ${f.phone}`,
    `Email: ${f.email}`,
    `Interested Package: ${f.package}`,
    "",
    "Message:",
    f.message,
  ].join("\n");
  return `mailto:${SITE.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function SponsorPage() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    package: SITE.sponsorPackages[0].name,
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sponsor-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    } catch {
      /* lead save is best-effort; the email enquiry below always works */
    }
    setSubmitted(true);
    window.location.href = buildMailto(form);
  };

  return (
    <div className="min-h-screen bg-[#080705] text-[#F7F4EF]">
      <header className="border-b border-[#D4AF37]/15">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
          <a href="/" data-testid="sponsor-home-link" className="flex items-center">
            <img src={SITE.brand.logoSmall} alt={SITE.brand.logoAlt} className="h-12 w-auto" />
          </a>
          <a
            href="/"
            data-testid="sponsor-back-link"
            className="rounded-full border border-[#D4AF37]/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
          >
            Back to Site
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden px-5 py-16 text-center sm:px-8 sm:py-24">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,175,55,0.12),transparent_60%)]"
            aria-hidden="true"
          />
          <p dir="rtl" lang="ur" className="font-urdu relative text-2xl text-[#D4AF37] sm:text-3xl">
            {SITE.urdu}
          </p>
          <h1 data-testid="sponsor-heading" className="font-display gold-text relative mt-4 text-4xl sm:text-5xl lg:text-6xl">
            Become a Sponsor
          </h1>
          <p className="font-accent relative mx-auto mt-5 max-w-2xl text-lg italic leading-relaxed text-[#C8C2B7] sm:text-xl">
            Place your brand inside a royal mehfil — {SITE.tagline}, {SITE.date},{" "}
            {SITE.venue.name}, {SITE.venue.city}.
          </p>
        </section>

        <section data-testid="sponsor-packages" className="mx-auto max-w-6xl px-5 pb-8 sm:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {SITE.sponsorPackages.map((p) => (
              <div
                key={p.id}
                data-testid={`package-card-${p.id}`}
                className="flex h-full flex-col rounded-3xl border border-[#D4AF37]/25 bg-[#161310] p-8 text-center transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-[#D4AF37]/60 hover:shadow-[0_24px_60px_-24px_rgba(212,175,55,0.35)]"
              >
                <StarOrnament className="mx-auto h-6 w-6 text-[#D4AF37]/70" />
                <h2 className="font-display mt-4 text-2xl text-[#F7F4EF]">{p.name}</h2>
                <p data-testid={`package-price-${p.id}`} className="font-display gold-text mt-4 text-3xl sm:text-4xl">
                  {p.price}
                </p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-[#918A7E] sm:text-base">
                  A curated share of the sponsorship inventory below — final entitlements are
                  tailored with you.
                </p>
                <a
                  href="#sponsor-enquiry"
                  data-testid={`package-enquire-${p.id}`}
                  onClick={() => setForm({ ...form, package: p.name })}
                  className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] px-7 text-sm font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/[0.14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65]"
                >
                  Enquire
                </a>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="sponsor-benefits" className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
          <h2 className="font-display text-center text-3xl text-[#F7F4EF] sm:text-4xl">
            Sponsorship Benefits
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-base leading-relaxed text-[#918A7E]">
            Drawn from our existing sponsorship inventory for {SITE.eventName}.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SITE.sponsorBenefits.map((b, i) => (
              <div
                key={b}
                data-testid={`benefit-${i + 1}`}
                className="flex items-center gap-3 rounded-2xl border border-[#D4AF37]/20 bg-[#161310] px-5 py-4"
              >
                <StarOrnament className="h-3.5 w-3.5 shrink-0 text-[#D4AF37]" />
                <span className="text-sm text-[#C8C2B7] sm:text-base">{b}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="sponsor-enquiry" data-testid="sponsor-enquiry" className="mx-auto max-w-3xl px-5 pb-20 sm:px-8">
          <div className="rounded-3xl border border-[#D4AF37]/30 bg-[#0E0C0A] p-8 sm:p-12">
            <h2 className="font-display text-center text-3xl text-[#F3E5AB] sm:text-4xl">
              Enquire About Sponsorship
            </h2>
            <p className="mt-3 text-center text-sm text-[#918A7E] sm:text-base">
              Send your details — the enquiry opens your email app, addressed to{" "}
              <span className="text-[#F3E5AB]">{SITE.contactEmail}</span>.
            </p>
            {submitted ? (
              <div data-testid="sponsor-success" className="mt-8 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-8 text-center">
                <p className="font-display text-2xl text-[#F3E5AB]">
                  Thank you. Our sponsorship team will contact you shortly.
                </p>
              </div>
            ) : (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="sp-name" className={LABEL}>Name *</label>
                <input id="sp-name" data-testid="sponsor-form-name" required value={form.name} onChange={set("name")} className={INPUT} placeholder="Your full name" />
              </div>
              <div>
                <label htmlFor="sp-company" className={LABEL}>Company</label>
                <input id="sp-company" data-testid="sponsor-form-company" value={form.company} onChange={set("company")} className={INPUT} placeholder="Company or brand" />
              </div>
              <div>
                <label htmlFor="sp-phone" className={LABEL}>Phone *</label>
                <input id="sp-phone" data-testid="sponsor-form-phone" required type="tel" value={form.phone} onChange={set("phone")} className={INPUT} placeholder="+91 …" />
              </div>
              <div>
                <label htmlFor="sp-email" className={LABEL}>Email *</label>
                <input id="sp-email" data-testid="sponsor-form-email" required type="email" value={form.email} onChange={set("email")} className={INPUT} placeholder="you@company.com" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sp-package" className={LABEL}>Interested Package</label>
                <select id="sp-package" data-testid="sponsor-form-package" value={form.package} onChange={set("package")} className={INPUT}>
                  {SITE.sponsorPackages.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name} — {p.price}
                    </option>
                  ))}
                  <option value="Not sure yet">Not sure yet — advise me</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sp-message" className={LABEL}>Message</label>
                <textarea id="sp-message" data-testid="sponsor-form-message" rows={4} value={form.message} onChange={set("message")} className={`${INPUT} py-3`} placeholder="Tell us about your brand and goals…" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="sponsor-form-submit"
                  className="btn-gold min-h-[56px] w-full rounded-full text-base font-bold uppercase tracking-[0.12em] text-[#0A0805] transition-[transform,box-shadow,background-position] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-lg"
                >
                  Enquire About Sponsorship
                </button>
              </div>
            </form>
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-[#D4AF37]/15 px-5 py-8 text-center text-sm text-[#918A7E]">
        © {new Date().getFullYear()} {SITE.eventName} · Event by {SITE.organizer} ·{" "}
        <a href={`mailto:${SITE.contactEmail}`} className="text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#E6CA65]">
          {SITE.contactEmail}
        </a>
      </footer>
    </div>
  );
}
